import React, { createContext, useContext, useState, useCallback } from 'react'

export type A11yContextType = {
  announce: (message: string, priority?: 'polite' | 'assertive') => void
}

export const A11yContext = createContext<A11yContextType | undefined>(undefined)

export const useA11y = () => {
  const context = useContext(A11yContext)

  if (!context) {
    throw new Error('useA11y must be used within A11yProvider')
  }
  return context
}

type A11yProviderProps = {
  children: React.ReactNode
}

export const A11yProvider = ({ children }: A11yProviderProps) => {
  const [politeMessage, setPoliteMessage] = useState('')
  const [assertiveMessage, setAssertiveMessage] = useState('')

  // Announce status updates to screen readers
  const announce = useCallback(
    (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      const setMessage =
        priority === 'assertive' ? setAssertiveMessage : setPoliteMessage
      // Clear first so screen readers detect the change even for repeated messages
      setMessage('')
      requestAnimationFrame(() => {
        setMessage(message)
      })
    },
    []
  )

  return (
    <A11yContext.Provider value={{ announce }}>
      {children}
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className='screenReaderOnly'
      >
        {politeMessage}
      </div>
      <div
        role='alert'
        aria-live='assertive'
        aria-atomic='true'
        className='screenReaderOnly'
      >
        {assertiveMessage}
      </div>
    </A11yContext.Provider>
  )
}
